/**
 * Pontos de parada do itinerário do QCO e importação por CSV — PBI 113368.
 * Contrato: 113368-api-contract-importacao-pontos-parada-qco (§1 importação, §2 pontos).
 * Irmão do PBI 112887: o envelope da importação é o dos itens (`item-itinerario.interface`).
 */
import {
  IDadosImportacaoItinerario,
  IImportarItinerarioRequest,
  IRegistroImportacaoItinerario,
  IValidacaoImportacaoItinerario,
} from './item-itinerario.interface';

/** Ponto da sub-aba "Pontos de Parada" (§2.1 listagem e §2.2 consulta). */
export interface IPontoParada {
  id: string;
  ordem: number;
  cep: string;
  logradouro: string;
  municipio: string;
  estado: string;
  /**
   * O back serializa com `NON_NULL`: bairro, coordenadas, Número, Km e rastro
   * da importação **somem** do JSON quando vazios. Ausência é célula vazia.
   * `numero` falta no ponto só com Km e no ponto anterior ao campo (gravado com
   * `0`, que o back omite — regra R7 do contrato 113368).
   */
  bairro?: string;
  latitude?: number;
  longitude?: number;
  numero?: number;
  km?: number;
  importado?: boolean;
  nomeUsuarioImportacao?: string;
  nomeArquivoImportacao?: string;
  dataImportacao?: string;
}

/**
 * Corpo do POST (§2.3) e do PUT (§2.4) do ponto. Precisa de Número ou Km.
 * No PUT, `null` **apaga** o valor gravado — o PUT substitui o ponto inteiro.
 */
export type PontoParadaRequest = Pick<
  IPontoParada,
  'logradouro' | 'municipio' | 'estado' | 'cep'
> & {
  bairro: string | null;
  numero: number | null;
  km: number | null;
  latitude: number | null;
  longitude: number | null;
};

/** As 9 colunas do arquivo, já normalizadas pelo back (§1.1 `dados`). */
export interface IDadosImportacaoPontoParada extends IDadosImportacaoItinerario {
  /** Sem zeros à esquerda; `null` quando vazio. */
  numero: string | null;
  /** Ponto decimal (`"12.5"`); `null` quando vazio. */
  km: string | null;
}

export type IRegistroImportacaoPontoParada =
  IRegistroImportacaoItinerario<IDadosImportacaoPontoParada>;

export type IValidacaoImportacaoPontoParada =
  IValidacaoImportacaoItinerario<IDadosImportacaoPontoParada>;

export type IImportarPontosParadaRequest =
  IImportarItinerarioRequest<IDadosImportacaoPontoParada>;

/**
 * Km exibido com vírgula decimal e sem zeros à direita (`12.5` e `"12.50"` → `12,5`); ausente vira célula
 * vazia. Na verificação da importação o Km chega como texto, na escala do arquivo.
 */
export function formatarKm(km: number | string | null | undefined): string {
  if (km === null || km === undefined || km === '') return '';
  const valor = Number(km);
  return (Number.isFinite(valor) ? String(valor) : String(km)).replace('.', ',');
}
